import { useEffect, useRef, useState } from 'react';

const DEFAULT_EXIT_MS = 160;

/**
 * Keep content mounted through a close animation.
 * @param {boolean} open
 * @param {{ exitMs?: number }} [options]
 * @returns {{ present: boolean, closing: boolean }}
 */
export function usePresenceTransition(open, options = {}) {
  const exitMs = options.exitMs ?? DEFAULT_EXIT_MS;
  const [present, setPresent] = useState(() => Boolean(open));
  const [closing, setClosing] = useState(false);
  const presentRef = useRef(present);
  presentRef.current = present;

  useEffect(() => {
    if (typeof window === 'undefined') {
      setPresent(Boolean(open));
      setClosing(false);
      return undefined;
    }

    if (open) {
      setPresent(true);
      setClosing(false);
      return undefined;
    }

    if (!presentRef.current) return undefined;

    setClosing(true);
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delay = reduced ? 0 : exitMs;
    const timerId = window.setTimeout(() => {
      setPresent(false);
      setClosing(false);
    }, delay);
    return () => window.clearTimeout(timerId);
  }, [open, exitMs]);

  return { present, closing };
}

export default usePresenceTransition;
